<template>
  <div class="page mini-game-selection">
    <h1 class="page-title">Choose games</h1>
    <p class="page-lede">
      Each player picks every game you choose here once.
      <template v-if="selectedMiniGames.length">
        With {{ playerCount }} players and {{ selectedMiniGames.length }} game{{
          selectedMiniGames.length === 1 ? '' : 's'
        }}, that's {{ playerCount * selectedMiniGames.length }} rounds.
      </template>
    </p>

    <div class="toolbar">
      <button type="button" class="link-button" @click="toggleAll">
        {{ allSelected ? 'Clear all' : 'Select all' }}
      </button>
    </div>

    <ul class="games">
      <li v-for="game in availableMiniGames" :key="game.id">
        <label class="game-option" :class="{ 'is-checked': selectedMiniGames.includes(game.id) }">
          <input v-model="selectedMiniGames" type="checkbox" :value="game.id" />
          <GameIndex :symbol="game.symbol" />
          <span class="game-text">
            <span class="game-name">{{ game.displayName }}</span>
            <span class="game-rule">{{ ruleText(game, playerCount) }}</span>
          </span>
          <span class="check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M5 12.5l4.5 4.5L19 7.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </label>
      </li>
    </ul>

    <button
      class="btn btn-primary btn-block"
      :disabled="selectedMiniGames.length === 0"
      @click="startMiniGame"
    >
      Start game
    </button>
    <p v-if="selectedMiniGames.length === 0" class="hint">Pick at least one game.</p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useGameStore } from '../stores/game';
import { useGameConfigStore } from '../stores/gameConfig';
import { ruleText } from '../utils/gameRules';
import GameIndex from '../components/GameIndex.vue';

const router = useRouter();
const store = useGameStore();
const gameConfig = useGameConfigStore();

if (store.players.length === 0) router.replace('/new-game');

const availableMiniGames = Object.values(gameConfig.games);
const selectedMiniGames = ref([]);
const playerCount = computed(() => store.players.length);

const allSelected = computed(() => selectedMiniGames.value.length === availableMiniGames.length);

const toggleAll = () => {
  selectedMiniGames.value = allSelected.value ? [] : availableMiniGames.map((game) => game.id);
};

const startMiniGame = () => {
  if (selectedMiniGames.value.length > 0) {
    const ordered = availableMiniGames
      .map((game) => game.id)
      .filter((id) => selectedMiniGames.value.includes(id));
    store.setMiniGames(ordered);
    router.push('/game');
  }
};
</script>

<style scoped>
.page-lede {
  margin-bottom: 1rem;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.link-button {
  min-height: 40px;
  padding: 0 0.25rem;
  color: var(--on-table);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: var(--on-table-muted);
}

.games {
  list-style: none;
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.game-option {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.625rem 0.875rem 0.625rem 0.625rem;
  border-radius: var(--radius-card);
  border: 1px solid transparent;
  background-color: var(--table-raised);
  cursor: pointer;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.game-option:hover {
  background-color: var(--table-hover);
}

.game-option.is-checked {
  border-color: var(--accent);
}

.game-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.game-option:has(input:focus-visible) {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.game-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.game-name {
  font-weight: 600;
}

.game-rule {
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.check {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  border: 1.5px solid var(--on-table-muted);
  color: transparent;
}

.is-checked .check {
  background-color: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}

.hint {
  margin-top: 0.5rem;
  text-align: center;
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

@media (min-width: 640px) {
  .games {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
